import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Planet3D from "@/components/Planet3D";
import { ArrowRight, Sparkles, ShieldCheck, Package, Star, BadgeCheck, Frame as FrameIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "@/lib/content";
import api from "@/lib/api";

export default function Home() {
  const overline = useT("home.hero.overline", "A LUXURY REGISTRY · EST. 2026");
  const t1 = useT("home.hero.title_1", "Own a Piece");
  const t2 = useT("home.hero.title_2", "of the");
  const tGold = useT("home.hero.title_gold", "Cosmos.");
  const body = useT("home.hero.body", "Teuralink offers commemorative land registration on the Moon and Mars.");
  const ctaMoon = useT("home.cta.moon", "Explore the Moon");
  const ctaMars = useT("home.cta.mars", "Explore Mars");
  const stat1k = useT("home.stat1.k", "10,000+");
  const stat1v = useT("home.stat1.v", "Plots / marker");
  const stat2k = useT("home.stat2.k", "₹999+");
  const stat2v = useT("home.stat2.v", "Starting price");
  const stat3k = useT("home.stat3.k", "48hrs");
  const stat3v = useT("home.stat3.v", "Ships within");
  const valueEyebrow = useT("home.value.eyebrow", "Why Teuralink");
  const valueTitle = useT("home.value.title", "A cinematic keepsake, engineered for permanence.");
  const disclaimer = useT("home.disclaimer",
    "Teuralink is a private commemorative registry offering symbolic Moon and Mars-themed gift experiences.");

  const valueCards = [
    { Icon: Star, k1: "home.value.card1.title", k2: "home.value.card1.body",
      fT: "Signature Locations", fB: "Curated craters, planitiae, and lunar seas." },
    { Icon: Package, k1: "home.value.card2.title", k2: "home.value.card2.body",
      fT: "Hand-Finished Deed", fB: "Premium printed registration deed couriered to you." },
    { Icon: ShieldCheck, k1: "home.value.card3.title", k2: "home.value.card3.body",
      fT: "Public Verification", fB: "Every registration verifiable on our public registry." },
  ];

  return (
    <div className="relative z-10">
      {/* HERO */}
      <section className="min-h-screen pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-gold mb-6" data-testid="hero-overline">{overline}</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tighter" data-testid="hero-title">
              {t1} <br />
              {t2} <span className="text-gold">{tGold}</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed" data-testid="hero-body">
              {body}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/explore/moon" className="btn-primary" data-testid="cta-moon">
                {ctaMoon} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/explore/mars" className="btn-ghost" data-testid="cta-mars">
                {ctaMars} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/verify" className="btn-ghost" data-testid="cta-verify">
                <BadgeCheck className="w-4 h-4" /> Verify Your Registration
              </Link>
              <Link to="/frames" className="btn-ghost" data-testid="cta-frames">
                <FrameIcon className="w-4 h-4" /> Buy Frames · ₹499
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              <StatCell k={stat1k} v={stat1v} testid="stat-1" />
              <StatCell k={stat2k} v={stat2v} testid="stat-2" />
              <StatCell k={stat3k} v={stat3v} testid="stat-3" />
            </div>
          </div>

          <div className="relative h-[360px] sm:h-[480px] lg:h-[640px] mt-6 lg:mt-0">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(212,175,55,0.16), rgba(212,175,55,0.05) 45%, transparent 70%)",
              }}
            />
            <div className="absolute inset-0">
              <Planet3D planet="moon" autoRotate rotationSpeed={0.04} cameraZ={3.6} />
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="px-6 lg:px-12 py-24 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-4">{valueEyebrow}</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
            {valueTitle}
          </h2>

          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {valueCards.map(({ Icon, k1, k2, fT, fB }) => (
              <ValueCard key={k1} Icon={Icon} titleKey={k1} bodyKey={k2} fT={fT} fB={fB} />
            ))}
          </div>
        </div>
      </section>

      {/* PLANETS BLOCK */}
      <section className="px-6 lg:px-12 py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
          <PlanetCard planet="moon" title="The Moon" tagline="Silver-grey serenity" href="/explore/moon" />
          <PlanetCard planet="mars" title="Mars" tagline="The red frontier" href="/explore/mars" />
        </div>
      </section>

      {/* FRAMES CAROUSEL */}
      <FramesCarousel />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* DISCLAIMER */}
      <section className="px-6 lg:px-12 py-16">
        <div className="max-w-5xl mx-auto glass-dark rounded-2xl p-8 border border-white/10 flex flex-col md:flex-row gap-6 items-start">
          <Sparkles className="w-6 h-6 text-gold shrink-0 mt-1" />
          <div>
            <div className="overline mb-2">A note on ownership</div>
            <p className="text-white/75 leading-relaxed" data-testid="home-disclaimer">
              {disclaimer}
              <Link to="/legal" className="text-gold ml-2 underline">Read the full disclaimer →</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCell({ k, v, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-display text-2xl font-bold">{k}</div>
      <div className="text-xs text-white/50 mt-1">{v}</div>
    </div>
  );
}

function ValueCard({ Icon, titleKey, bodyKey, fT, fB }) {
  const title = useT(titleKey, fT);
  const body = useT(bodyKey, fB);
  return (
    <div className="glass rounded-2xl p-8 hover:border-white/25 transition-all">
      <Icon className="w-6 h-6 text-gold" />
      <div className="font-display text-2xl font-bold mt-6 tracking-tight">{title}</div>
      <p className="text-white/60 mt-3 leading-relaxed text-sm">{body}</p>
    </div>
  );
}

function PlanetCard({ planet, title, tagline, href }) {
  return (
    <Link to={href} data-testid={`planet-card-${planet}`} className="group relative rounded-3xl overflow-hidden glass hover:border-white/25 transition-all block">
      <div className="h-96">
        <Planet3D planet={planet} autoRotate />
      </div>
      <div className="p-8 border-t border-white/10 flex items-end justify-between">
        <div>
          <div className="overline">{tagline}</div>
          <div className="font-display text-3xl font-bold tracking-tight mt-1">{title}</div>
        </div>
        <div className="btn-ghost group-hover:bg-white group-hover:text-black">
          Explore <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

const TESTIMONIALS = [
  {
    name: "Ayush Sharma",
    initials: "AS",
    accent: "from-sky-400/40 to-indigo-800/40",
    dot: "#7cc4ff",
    acres: 1,
    planet: "moon",
    location: "Sea of Tranquility",
    regNumber: "TLK-M-2026-104829",
    quote:
      "The certificate arrived beautifully packaged — the most memorable birthday gift I've ever received.",
  },
  {
    name: "Disha Bhowmick",
    initials: "DB",
    accent: "from-fuchsia-400/40 to-purple-800/40",
    dot: "#e2a4ff",
    acres: 3,
    planet: "moon",
    location: "Mare Serenitatis",
    regNumber: "TLK-M-2026-118374",
    quote:
      "Owning a piece of the Moon has been my dream since childhood. The framed deed is now my prized possession.",
  },
  {
    name: "Bishal Kumar",
    initials: "BK",
    accent: "from-orange-500/40 to-red-800/40",
    dot: "#ffab7a",
    acres: 5,
    planet: "mars",
    location: "Valles Marineris",
    regNumber: "TLK-R-2026-227915",
    quote:
      "The Mars deed is stunning — a genuine conversation starter for every visitor to my office.",
  },
];

function FramesCarousel() {
  const [frames, setFrames] = useState([]);
  const [idx, setIdx] = useState(0);
  const [perView, setPerView] = useState(1);

  useEffect(() => {
    api.get("/frames")
      .then((r) => setFrames((r.data || []).slice(0, 4)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 1024) setPerView(3);
      else if (w >= 640) setPerView(2);
      else setPerView(1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    if (frames.length === 0) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % frames.length);
    }, 4500);
    return () => clearInterval(id);
  }, [frames.length]);

  const total = frames.length;
  const maxStart = Math.max(0, total - perView);
  const clampedIdx = Math.min(idx, maxStart);
  const step = 100 / perView;
  const translate = -(clampedIdx * step);

  if (total === 0) return null;

  const go = (dir) => {
    setIdx((i) => {
      const next = i + dir;
      if (next < 0) return maxStart;
      if (next > maxStart) return 0;
      return next;
    });
  };

  return (
    <section
      className="px-6 lg:px-12 py-24 relative z-10"
      data-testid="home-frames-carousel"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline text-gold mb-3">Teuralink Framed Prints</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              Cosmic Frames <span className="text-gold">.</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl">
              Hand-finished planetary posters in premium wooden frames — flat{" "}
              <span className="text-gold font-mono-space">₹499</span> across the range.
            </p>
          </div>
          <div className="hidden sm:flex gap-2">
            <button
              onClick={() => go(-1)}
              aria-label="Previous"
              data-testid="frames-carousel-prev"
              className="w-11 h-11 rounded-full glass border border-white/10 hover:border-[#D4AF37]/40 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next"
              data-testid="frames-carousel-next"
              className="w-11 h-11 rounded-full glass border border-white/10 hover:border-[#D4AF37]/40 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(${translate}%)` }}
          >
            {frames.map((f) => (
              <Link
                key={f.id}
                to={`/frames/${f.id}`}
                data-testid={`home-frame-${f.id}`}
                className="shrink-0 px-3 group"
                style={{ width: `${step}%` }}
              >
                <div className="glass rounded-3xl p-4 hover:border-[#D4AF37]/40 transition-all h-full">
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-black/40">
                    <img
                      src={f.image_url}
                      alt={`Teuralink ${f.planet} framed print`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                    <div className="absolute top-3 right-3 bg-black/70 backdrop-blur px-3 py-1.5 rounded-full text-xs font-mono-space text-gold border border-[#D4AF37]/30">
                      ₹{f.price_inr}
                    </div>
                  </div>
                  <div className="mt-4 px-1 pb-2">
                    <div className="overline text-white/50">Framed Print</div>
                    <div className="font-display text-xl font-bold tracking-tight mt-1">{f.planet}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-mono-space text-base text-gold">₹{f.price_inr}</span>
                      <span className="text-xs text-white/50 group-hover:text-white transition-colors">View & Buy →</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Dot indicators */}
        <div className="mt-6 flex justify-center gap-1.5">
          {Array.from({ length: maxStart + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`frames-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === clampedIdx ? "bg-gold w-8" : "bg-white/20 w-3 hover:bg-white/40"
              }`}
            />
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Link to="/frames" className="btn-primary" data-testid="home-view-all-frames">
            <FrameIcon className="w-4 h-4" /> View All Frames <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}


function Testimonials() {
  return (
    <section
      className="px-6 lg:px-12 py-24 relative z-10"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="overline text-gold mb-3">From our registry</div>
        <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
          Owners of the <span className="text-gold">cosmos</span>.
        </h2>
        <p className="text-white/60 mt-4 max-w-2xl">
          A handful of the people who now hold a Teuralink deed — from their own words.
        </p>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <TestimonialCard key={t.initials} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  const planetLabel = t.planet === "moon" ? "The Moon" : "Mars";
  return (
    <div
      className="glass rounded-3xl p-6 lg:p-7 border border-white/10 flex flex-col gap-5 hover:border-white/25 transition-all"
      data-testid={`testimonial-${t.initials.toLowerCase()}`}
    >
      {/* Header — avatar + name */}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <div
            className={`w-14 h-14 rounded-full bg-gradient-to-br ${t.accent} border border-white/20 flex items-center justify-center font-display text-lg font-bold text-white`}
          >
            {t.initials}
          </div>
          <span
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-[#0e0e14]"
            style={{ background: t.dot }}
            aria-hidden="true"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-bold text-white truncate">
            {t.name}
          </div>
          <div className="text-xs text-white/60 mt-0.5">
            Purchased {t.acres} {t.acres === 1 ? "Acre" : "Acres"} on {planetLabel}
          </div>
        </div>
      </div>

      {/* Mini certificate preview */}
      <MiniCertificate t={t} />

      {/* Quote */}
      <p className="text-sm text-white/80 leading-relaxed italic">
        “{t.quote}”
      </p>
    </div>
  );
}

function MiniCertificate({ t }) {
  const planetLabel = t.planet === "moon" ? "The Moon" : "Mars";
  const planetBg =
    t.planet === "moon"
      ? "radial-gradient(circle at 38% 34%, #eaeaea 0%, #b0b0b0 30%, #6a6a6a 65%, #2a2a2a 100%)"
      : "radial-gradient(circle at 38% 34%, #f0b48a 0%, #c46b3d 32%, #8b3820 62%, #3b1a10 100%)";
  return (
    <div className="relative rounded-xl overflow-hidden border border-[#D4AF37]/40 shadow-[0_10px_40px_-15px_rgba(212,175,55,0.35)]">
      {/* Parchment background with corner diamonds */}
      <div className="relative bg-[#EDE4CC] text-[#0A0A0C] p-4 pb-3">
        {/* Diamonds */}
        {[
          "top-1 left-1",
          "top-1 right-1",
          "bottom-1 left-1",
          "bottom-1 right-1",
        ].map((pos, i) => (
          <span
            key={i}
            className={`absolute ${pos} w-1.5 h-1.5 rotate-45 bg-[#D4AF37]`}
            aria-hidden="true"
          />
        ))}
        {/* Watermark */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          aria-hidden="true"
        >
          <span
            className="font-display font-bold text-[#0A0A0C]/[0.04]"
            style={{ fontSize: "40px", transform: "rotate(-18deg)" }}
          >
            TEURALINK
          </span>
        </div>

        {/* Header */}
        <div className="relative flex items-center justify-between">
          <div className="font-display text-[11px] tracking-[0.15em] font-bold">
            TEURALINK
          </div>
          <div className="text-[8px] text-[#5C5B57] tracking-[0.18em]">
            CERTIFICATE · A4
          </div>
        </div>
        <div className="mt-1 h-px bg-[#D4AF37]/60 relative" />

        {/* Body */}
        <div className="relative mt-3 flex gap-3 items-start">
          <div
            className="w-14 h-14 rounded-full ring-2 ring-[#D4AF37] shrink-0"
            style={{ background: planetBg }}
            aria-hidden="true"
          >
            {/* Target crosshair */}
            <div className="w-full h-full relative">
              <span className="absolute top-1/2 left-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/80" />
              <span className="absolute top-1/2 left-1/2 w-6 h-px -translate-x-1/2 -translate-y-1/2 bg-white/60" />
              <span className="absolute top-1/2 left-1/2 w-px h-6 -translate-x-1/2 -translate-y-1/2 bg-white/60" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-[8px] uppercase tracking-widest text-[#5C5B57]">
              Registered To
            </div>
            <div className="text-[11px] font-bold uppercase tracking-wide truncate">
              {t.name}
            </div>
            <div className="mt-1.5 text-[8px] uppercase tracking-widest text-[#5C5B57]">
              Reg. No.
            </div>
            <div className="font-mono-space text-[10.5px] font-bold text-[#8a6d13] truncate">
              {t.regNumber}
            </div>
          </div>
        </div>

        {/* Footer strip — gold band */}
        <div className="relative mt-3 grid grid-cols-3 gap-1 text-[9px] rounded-md bg-[#0A0A0C] text-[#EDE4CC] px-2 py-1.5">
          <div>
            <div className="text-[7px] tracking-widest text-[#EDE4CC]/60">
              PLANET
            </div>
            <div className="text-[9px] font-bold text-[#D4AF37]">{planetLabel}</div>
          </div>
          <div>
            <div className="text-[7px] tracking-widest text-[#EDE4CC]/60">
              SIZE
            </div>
            <div className="text-[9px] font-bold">
              {t.acres} {t.acres === 1 ? "Acre" : "Acres"}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[7px] tracking-widest text-[#EDE4CC]/60">
              LOCATION
            </div>
            <div className="text-[9px] font-bold truncate">{t.location}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

